import { toast } from "sonner";
import {
  PAIRING_CHANNEL_KINDS,
  TELEGRAM_CONFIGURE_KINDS,
  LINKEDIN_CONFIGURE_KINDS,
  REDDIT_OAUTH_KINDS,
  QUORA_CONFIGURE_KINDS,
} from "@/lib/channelRegistry";
import { connectionDisplayStatus } from "@/lib/connectionStatus";
import { pairingErrorDescription, openclawPairingMessage, pairingUserMessage } from "@/lib/pairingErrors";

function oauthErrorDescription(e) {
  const hint = e?.data?.detail?.error?.setup_instructions;
  if (Array.isArray(hint)) return hint.join(" ");
  if (typeof hint === "string") return hint;
  return undefined;
}

async function startOAuthFlow(startFn, returnPath, errorLabel, { beforeRedirect } = {}) {
  try {
    const { oauthApi } = await import("@/lib/api");
    const r = await startFn(oauthApi, returnPath);
    if (r?.auth_url) {
      beforeRedirect?.(r);
      window.location.href = r.auth_url;
    } else toast.error(`${errorLabel} did not return an authorization URL`);
  } catch (e) {
    toast.error(e?.message || `Couldn't start ${errorLabel}`, {
      description: oauthErrorDescription(e),
    });
  }
}

function requireConnectionRow(row) {
  if (row?.id) return true;
  toast.error("Connection row missing — refresh the page and try again");
  return false;
}

async function handleGmailAction() {
  await startOAuthFlow(
    (oauthApi, path) => oauthApi.startGoogle(path),
    "/settings/connections",
    "Google OAuth",
  );
}

async function handleRedditAction() {
  await startOAuthFlow(
    (oauthApi, path) => oauthApi.startReddit(path),
    "/settings/connections",
    "Reddit OAuth",
  );
}

async function handleMetaFacebookAction() {
  await startOAuthFlow(
    (oauthApi, path) => oauthApi.startFacebook(path),
    "/settings/connections",
    "Facebook Login",
  );
}

async function handleMetaInstagramAction() {
  await startOAuthFlow(
    (oauthApi, path) => oauthApi.startInstagram(path),
    "/settings/connections",
    "Instagram Login",
    {
      beforeRedirect(r) {
        if (!r?.redirect_uri) return;
        toast.message("Instagram redirect URI (must match Meta Business login settings)", {
          description: r.redirect_uri,
          duration: 12000,
        });
      },
    },
  );
}

async function handleTelegramAction(c, row, ctx) {
  if (!requireConnectionRow(row)) return;
  if (connectionDisplayStatus(row, false) === "sync_failed") {
    ctx.setTelegramSyncBusy(true);
    try {
      const { channelsApi } = await import("@/lib/api");
      const r = await channelsApi.syncTelegramOpenclaw(row.id);
      await ctx.fetchRows();
      if (r?.openclaw_synced) {
        toast.success("Telegram synced with OpenClaw");
      } else {
        toast.error("OpenClaw sync failed", {
          description: r?.openclaw_error || r?.openclaw_warning || "Retry in a few seconds.",
        });
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't sync Telegram with OpenClaw");
    } finally {
      ctx.setTelegramSyncBusy(false);
    }
    return;
  }
  ctx.setTelegramOpen(true);
}

async function handlePairingAction(c, row, ctx) {
  if (!requireConnectionRow(row)) return;
  ctx.setPairingBusy(true);
  try {
    const { channelsApi, onboardingApi } = await import("@/lib/api");
    const oc = await onboardingApi.openclawStatus().catch(() => null);
    if (oc && !oc.gateway_reachable) {
      toast.error("OpenClaw gateway is not reachable", {
        description: openclawPairingMessage(oc),
      });
      return;
    }
    if (oc?.pairing_required) {
      toast.error("OpenClaw device approval required", {
        description: openclawPairingMessage(oc),
      });
      return;
    }
    ctx.setPairingConn({ id: row.id, kind: c.kind });
    ctx.setPairingPayload(null);
    const { newPairingClientSessionId } = await import("@/lib/pairingSession");
    const r = await channelsApi.pair(row.id, {
      client_session_id: newPairingClientSessionId(),
    });
    // Already-connected WhatsApp must open Manage — never the pairing success toast.
    if (
      c.kind === "whatsapp"
      && (r?.mode === "manage" || r?.status === "connected" || r?.already_connected)
      && typeof ctx.setWhatsappManageOpen === "function"
    ) {
      ctx.setPairingConn(null);
      ctx.setPairingPayload(null);
      ctx.setWhatsappManageRow?.(row);
      ctx.setWhatsappManageOpen(true);
      return;
    }
    ctx.setPairingPayload(r);
  } catch (e) {
    ctx.setPairingConn(null);
    ctx.setPairingPayload(null);
    toast.error(pairingUserMessage(e, `Couldn't start ${c.label} pairing`), {
      description: pairingErrorDescription(e),
    });
  } finally {
    ctx.setPairingBusy(false);
  }
}

async function handleWhatsAppAction(c, row, ctx) {
  if (!requireConnectionRow(row)) return;
  const status = connectionDisplayStatus(row, false);
  if (status === "connected") {
    ctx.setWhatsappManageOpen(true);
    ctx.setWhatsappManageRow(row);
    return;
  }
  await handlePairingAction(c, row, ctx);
}

export async function runConnectionChannelAction(c, row, ctx) {
  if (!ctx.canEdit) return;

  if (c.kind === "gmail") {
    await handleGmailAction();
    return;
  }
  if (REDDIT_OAUTH_KINDS.has(c.kind)) {
    await handleRedditAction();
    return;
  }
  if (c.kind === "facebook") {
    await handleMetaFacebookAction();
    return;
  }
  if (c.kind === "instagram") {
    await handleMetaInstagramAction();
    return;
  }
  if (QUORA_CONFIGURE_KINDS.has(c.kind)) {
    if (requireConnectionRow(row)) ctx.setQuoraOpen(true);
    return;
  }
  if (TELEGRAM_CONFIGURE_KINDS.has(c.kind)) {
    await handleTelegramAction(c, row, ctx);
    return;
  }
  if (LINKEDIN_CONFIGURE_KINDS.has(c.kind)) {
    if (requireConnectionRow(row)) ctx.setLinkedinOpen(true);
    return;
  }
  if (c.kind === "whatsapp") {
    await handleWhatsAppAction(c, row, ctx);
    return;
  }
  if (PAIRING_CHANNEL_KINDS.has(c.kind)) {
    await handlePairingAction(c, row, ctx);
  }
}

export async function disconnectConnectionChannel(c, row, fetchRows) {
  const { connectionsApi } = await import("@/lib/api");
  await connectionsApi.delete(row.id);
  // Soft disconnect keeps the connection row (status=disconnected). Only reseed
  // a placeholder when the API hard-deleted the row (non-inbox kinds).
  let list = [];
  try {
    list = (await fetchRows()) || [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`refresh after disconnect failed`, e?.message);
  }
  const stillPresent = list.some((r) => r?.id === row.id || r?.kind === c.kind);
  if (!stillPresent) {
    try {
      await connectionsApi.create(c.kind, { seeded_by: "settings_disconnect" });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`reseed ${c.kind} after disconnect failed`, e?.message);
    }
    await fetchRows();
  }
  toast.success(`${c.label} disconnected`);
}

function connectOrUpdateTitle(status, connectLabel, updateLabel) {
  return status === "connected" ? updateLabel : connectLabel;
}

export function connectionActionTitle(c, status, canEdit) {
  if (!canEdit) return "Admins only";
  if (c.kind === "gmail") {
    return connectOrUpdateTitle(status, "Connect Gmail via Google OAuth", "Reconnect Gmail via Google OAuth");
  }
  if (REDDIT_OAUTH_KINDS.has(c.kind)) {
    return connectOrUpdateTitle(status, "Connect Reddit via OAuth", "Reconnect Reddit via OAuth");
  }
  if (c.kind === "facebook") {
    return connectOrUpdateTitle(status, "Connect Facebook Page (Messenger)", "Reconnect Facebook");
  }
  if (c.kind === "instagram") {
    return connectOrUpdateTitle(status, "Connect with Instagram Login", "Reconnect Instagram");
  }
  if (QUORA_CONFIGURE_KINDS.has(c.kind)) {
    return connectOrUpdateTitle(status, "Connect Quora profile URL", "Update Quora profile");
  }
  if (TELEGRAM_CONFIGURE_KINDS.has(c.kind)) {
    if (status === "sync_failed") return "Retry OpenClaw sync without re-entering the bot token";
    return connectOrUpdateTitle(
      status,
      "Connect Telegram bot via BotFather token",
      "Update Telegram bot configuration",
    );
  }
  if (LINKEDIN_CONFIGURE_KINDS.has(c.kind)) {
    return connectOrUpdateTitle(
      status,
      "Connect LinkedIn profile URL",
      "Update LinkedIn profile configuration",
    );
  }
  if (PAIRING_CHANNEL_KINDS.has(c.kind)) {
    return connectOrUpdateTitle(status, `Pair ${c.label} via OpenClaw`, `Manage ${c.label} pairing`);
  }
  return c.tooltip;
}
